import React, { Component, Fragment } from 'react';
import { Layout, Typography, Button, Row, Col, Card, Descriptions, Tag, Spin, Table, message, Modal } from 'antd';
import { ArrowLeftOutlined, ReloadOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import AdminPortalHeader from '../../components/common/admin_portal_header';
import { getPaymentDetailsForAdmin, reprocessPaymentsForAdmin } from '../../api/pets-api';
import moment from 'moment';

const { Paragraph, Title } = Typography;
const { Content } = Layout;

class AdminPaymentDetails extends Component {
    constructor(props) {
        super(props);
        this.state = {
            paymentId: '',
            payment: null,
            loading: true,
            reprocessing: false,
        };
    }

    async componentWillMount() {
        var splittedPath = this.props.location.pathname.split('/');
        let id = splittedPath[splittedPath.length - 1];
        await this.setState({ paymentId: id });
        await this.getPayment(id);
    }

    async getPayment(id) {
        this.setState({ loading: true });
        try {
            // Use dedicated endpoint for payment details
            let respo = await getPaymentDetailsForAdmin(id);
            
            if (respo.status === 200 && respo.data.status === 'ok') {
                this.setState({ payment: respo.data.payment, loading: false });
            } else {
                this.setState({ loading: false });
                message.error('Failed to fetch payment details');
            }
        } catch (ex) {
            console.error('Error fetching payment:', ex);
            this.setState({ loading: false });
            if (ex.response && ex.response.status === 404) {
                message.error('Payment not found');
            } else {
                message.error('Error loading payment details');
            }
        }
    }

    refreshPayment = async () => {
        const { paymentId } = this.state;
        await this.getPayment(paymentId);
        message.success('Payment details refreshed');
    };

    reprocessPayment = () => {
        const { paymentId, payment } = this.state;
        
        Modal.confirm({
            title: 'Reprocess Payment',
            icon: <ExclamationCircleOutlined />,
            content: `Are you sure you want to reprocess payment ${payment.paymentRef || paymentId}?`,
            okText: 'Yes, Reprocess',
            cancelText: 'Cancel',
            onOk: async () => {
                this.setState({ reprocessing: true });
                try {
                    const response = await reprocessPaymentsForAdmin([paymentId]);
                    if (response.status === 200) {
                        message.success('Payment reprocessing initiated successfully');
                        // Refresh payment details after reprocessing
                        setTimeout(() => {
                            this.refreshPayment();
                        }, 1000);
                    } else {
                        message.error('Failed to reprocess payment');
                    }
                } catch (error) {
                    console.error('Error reprocessing payment:', error);
                    message.error('Error reprocessing payment');
                } finally {
                    this.setState({ reprocessing: false });
                }
            },
        });
    };

    navigateBack = () => {
        window.location.pathname = '/admin_payments';
    };

    getStatusTag(payment) {
        if (payment.paymentSuccess && payment.policyGeneratedSuccess) {
            return <Tag color="success">Success</Tag>;
        } else if (payment.paymentSuccess && !payment.policyGeneratedSuccess) {
            return <Tag color="warning">Payment Success - Policy Not Generated</Tag>;
        } else if (!payment.paymentSuccess) {
            return <Tag color="error">Payment Failed</Tag>;
        } else {
            return <Tag color="default">Pending</Tag>;
        }
    }

    render() {
        const { payment, loading, reprocessing } = this.state;

        if (loading) {
            return (
                <Layout className="dashboard payment-details">
                    <AdminPortalHeader />
                    <div className="m-2 text-center" style={{ paddingTop: '100px' }}>
                        <Spin size="large" />
                    </div>
                </Layout>
            );
        }

        if (!payment) {
            return (
                <Layout className="dashboard payment-details">
                    <AdminPortalHeader />
                    <div className="m-2">
                        <Button
                            type="primary"
                            icon={<ArrowLeftOutlined />}
                            onClick={this.navigateBack}
                            className="mb-3"
                        >
                            Back to Payments
                        </Button>
                        <Card>
                            <Title level={4}>Payment not found</Title>
                        </Card>
                    </div>
                </Layout>
            );
        }

        const petsColumns = [
            {
                title: 'Pet Name',
                dataIndex: 'name',
                key: 'name',
            },
            {
                title: 'Pet Type',
                dataIndex: 'petTypeId',
                key: 'petTypeId',
            },
            {
                title: 'Breed',
                dataIndex: 'breedId',
                key: 'breedId',
            },
            {
                title: 'Date of Birth',
                dataIndex: 'dob',
                key: 'dob',
                render: (dob) => (dob ? moment(dob).format('MM/DD/YYYY') : 'N/A'),
            },
            {
                title: 'Gender',
                dataIndex: 'gender',
                key: 'gender',
            },
        ];

        return (
            <Layout className="dashboard payment-details">
                <AdminPortalHeader />
                <div className="m-2">
                    <div className="mb-3">
                        <Button
                            type="primary"
                            icon={<ArrowLeftOutlined />}
                            onClick={this.navigateBack}
                            className="me-2"
                        >
                            Back to Payments
                        </Button>
                        <Button
                            type="default"
                            icon={<ReloadOutlined />}
                            onClick={this.refreshPayment}
                            loading={loading}
                            className="me-2"
                        >
                            Refresh
                        </Button>
                        {payment && payment.paymentSuccess !== false && !payment.policyGeneratedSuccess && (
                            <Button
                                type="primary"
                                danger
                                icon={<ExclamationCircleOutlined />}
                                onClick={this.reprocessPayment}
                                loading={reprocessing}
                            >
                                Reprocess Payment
                            </Button>
                        )}
                    </div>

                    <Title level={2} className="text-green font-600 ps-3 m-0">
                        Payment Details
                    </Title>

                    <Row gutter={15} className="pt-3">
                        <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                            <Card title="Payment Information" className="mb-3">
                                <Descriptions bordered column={2}>
                                    <Descriptions.Item label="Payment Reference">
                                        {payment.paymentRef || 'N/A'}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Status">
                                        {this.getStatusTag(payment)}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Amount">
                                        {payment.amount ? `${payment.amount.toFixed(2)}` : 'N/A'}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Payment Channel">
                                        {payment.paymentChannel || 'N/A'}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Payment Success">
                                        {payment.paymentSuccess ? (
                                            <Tag color="success">Yes</Tag>
                                        ) : (
                                            <Tag color="error">No</Tag>
                                        )}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Policy Generated">
                                        {payment.policyGeneratedSuccess ? (
                                            <Tag color="success">Yes</Tag>
                                        ) : (
                                            <Tag color="error">No</Tag>
                                        )}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Created Date">
                                        {moment(payment.createdAt).format('MM/DD/YYYY HH:mm:ss')}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Updated Date">
                                        {moment(payment.updatedAt).format('MM/DD/YYYY HH:mm:ss')}
                                    </Descriptions.Item>
                                </Descriptions>
                            </Card>

                            {payment.user && (
                                <Card title="User Information" className="mb-3">
                                    <Descriptions bordered column={2}>
                                        <Descriptions.Item label="First Name">
                                            {payment.user.firstName || 'N/A'}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Last Name">
                                            {payment.user.lastName || 'N/A'}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Email">
                                            {payment.user.email || payment.email || 'N/A'}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Phone Number">
                                            {payment.user.phoneNo || 'N/A'}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Nationality">
                                            {payment.user.nationality || 'N/A'}
                                        </Descriptions.Item>
                                        {payment.user.addresses && payment.user.addresses.length > 0 && (
                                            <Descriptions.Item label="Address" span={2}>
                                                {payment.user.addresses.map((addr, idx) => (
                                                    <div key={idx}>
                                                        {typeof addr === 'string' ? addr : JSON.stringify(addr)}
                                                    </div>
                                                ))}
                                            </Descriptions.Item>
                                        )}
                                    </Descriptions>
                                </Card>
                            )}

                            {payment.pets && payment.pets.length > 0 && (
                                <Card title="Pets Information" className="mb-3">
                                    <Table
                                        columns={petsColumns}
                                        dataSource={payment.pets}
                                        pagination={false}
                                        rowKey={(record, index) => index}
                                    />
                                </Card>
                            )}
                        </Col>
                    </Row>
                </div>
            </Layout>
        );
    }
}

export default AdminPaymentDetails;
